import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from '@/components/AuthShell';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { ApiError } from '@/lib/api';
import { authApi } from '@/features/auth/api';
import { useRefreshViewer } from '@/features/auth/AuthContext';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refreshViewer = useRefreshViewer();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.login(email, password);
      const viewer = await authApi.me();
      await refreshViewer();
      const next = params.get('next');
      const dest = next && next.startsWith('/') ? next : '/browse';
      // Multiple profiles → show the "Who's watching?" picker first.
      navigate(
        viewer.profiles.length > 1 ? `/profiles?next=${encodeURIComponent(dest)}` : dest,
        { replace: true },
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to keep bingeing."
      footer={
        <>
          New here?{' '}
          <Link to="/signup" className="font-semibold text-brand-soft hover:text-brand">
            Create an account
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {params.get('reset') === '1' && (
          <Alert tone="success">Password updated. Sign in with your new password.</Alert>
        )}
        {error && <Alert tone="error">{error}</Alert>}
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-xs font-medium text-ink-muted hover:text-ink"
          >
            Forgot password?
          </Link>
        </div>
        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthShell>
  );
}
